import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { LayoutService } from '../layout.service';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule],
  template: `
    <aside class="sidebar" [class.collapsed]="layoutService.isSidebarCollapsed()">
      <div class="sidebar-header">
        <div class="logo">
          <img src="assets/logo.png" alt="Cursive" class="logo-img" />
          @if (!layoutService.isSidebarCollapsed()) {
          <span class="logo-name">Cursive</span>
          }
        </div>
        <button
          class="collapse-toggle"
          (click)="layoutService.toggleSidebar()"
          [title]="layoutService.isSidebarCollapsed() ? 'Expand' : 'Collapse'"
        >
          <i
            class="pi"
            [class.pi-chevron-left]="!layoutService.isSidebarCollapsed()"
            [class.pi-chevron-right]="layoutService.isSidebarCollapsed()"
          ></i>
        </button>
      </div>

      <nav class="nav">
        <div class="nav-section">
          <a
            routerLink="/dashboard"
            routerLinkActive="active"
            [routerLinkActiveOptions]="{ exact: true }"
            class="nav-item"
            [title]="layoutService.isSidebarCollapsed() ? 'Overview' : ''"
          >
            <i class="pi pi-home"></i>
            @if (!layoutService.isSidebarCollapsed()) {
            <span>Overview</span>
            }
          </a>
          <a
            routerLink="/users"
            routerLinkActive="active"
            class="nav-item"
            [title]="layoutService.isSidebarCollapsed() ? 'Users' : ''"
          >
            <i class="pi pi-users"></i>
            @if (!layoutService.isSidebarCollapsed()) {
            <span>Users</span>
            }
          </a>
          <a
            routerLink="/sql-console"
            routerLinkActive="active"
            class="nav-item"
            [title]="layoutService.isSidebarCollapsed() ? 'SQL Console' : ''"
          >
            <i class="pi pi-database"></i>
            @if (!layoutService.isSidebarCollapsed()) {
            <span>SQL Console</span>
            }
          </a>
          <a
            routerLink="/system/feature-flags"
            routerLinkActive="active"
            class="nav-item"
            [title]="layoutService.isSidebarCollapsed() ? 'Feature Flags' : ''"
          >
            <i class="pi pi-flag"></i>
            @if (!layoutService.isSidebarCollapsed()) {
            <span>Feature Flags</span>
            }
          </a>
          <a
            routerLink="/system/promo-codes"
            routerLinkActive="active"
            class="nav-item"
            [title]="layoutService.isSidebarCollapsed() ? 'Promo Codes' : ''"
          >
            <i class="pi pi-ticket"></i>
            @if (!layoutService.isSidebarCollapsed()) {
            <span>Promo Codes</span>
            }
          </a>
          <a
            routerLink="/system/groups"
            routerLinkActive="active"
            class="nav-item"
            [title]="layoutService.isSidebarCollapsed() ? 'Groups' : ''"
          >
            <i class="pi pi-sitemap"></i>
            @if (!layoutService.isSidebarCollapsed()) {
            <span>Groups</span>
            }
          </a>
        </div>
      </nav>
    </aside>
  `,
  styles: [
    `
      .sidebar {
        display: flex;
        flex-direction: column;
        width: var(--sidebar-width);
        height: 100vh;
        background: var(--color-sidebar-bg);
        border-right: 1px solid var(--color-sidebar-border);
        transition: width var(--transition-base);
        position: relative;
        z-index: 100;
      }

      .sidebar.collapsed {
        width: var(--sidebar-collapsed);
      }

      .sidebar-header {
        padding: var(--space-3) var(--space-4);
        height: var(--header-height);
        display: flex;
        align-items: center;
        justify-content: space-between;
        border-bottom: 1px solid var(--color-sidebar-border);
      }

      .logo {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        min-width: 0;
      }

      .logo-img {
        width: 28px;
        height: 28px;
        object-fit: contain;
        flex-shrink: 0;
      }

      .logo-name {
        font-weight: 600;
        font-size: var(--font-size-base);
        color: var(--color-sidebar-text-active);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .nav {
        flex: 1;
        padding: var(--space-4);
        display: flex;
        flex-direction: column;
        gap: var(--space-6);
        overflow-y: auto;
      }

      .nav-section {
        display: flex;
        flex-direction: column;
        gap: var(--space-1);
      }

      .nav-item {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        padding: 8px var(--space-3);
        border-radius: var(--radius-md);
        color: var(--color-sidebar-text);
        text-decoration: none;
        font-size: var(--font-size-sm);
        font-weight: 500;
        transition: all var(--transition-fast);
        height: 36px;
      }

      .sidebar.collapsed .nav-item {
        justify-content: center;
        padding: 8px;
      }

      .nav-item:hover {
        background: var(--color-sidebar-hover);
        color: var(--color-sidebar-text-active);
      }

      .nav-item.active {
        background: var(--color-sidebar-active);
        color: var(--color-sidebar-text-active);
      }

      .nav-item i {
        font-size: 18px;
        width: 18px;
        text-align: center;
        flex-shrink: 0;
      }

      .nav-item.active i {
        color: var(--color-sidebar-text-active);
      }

      .collapse-toggle {
        width: 28px;
        height: 28px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        border: 1px solid var(--color-sidebar-border);
        border-radius: var(--radius-md);
        color: var(--color-sidebar-text);
        cursor: pointer;
        transition: all var(--transition-fast);
        flex-shrink: 0;
      }

      .collapse-toggle:hover {
        background: var(--color-sidebar-hover);
        color: var(--color-sidebar-text-active);
        border-color: var(--color-sidebar-hover);
      }

      .collapse-toggle i {
        font-size: 12px;
      }

      .sidebar.collapsed .sidebar-header {
        justify-content: center;
        padding: var(--space-3);
      }

      .sidebar.collapsed .logo {
        display: none;
      }

      .sidebar.collapsed .collapse-toggle {
        margin: 0;
      }
    `,
  ],
})
export class Sidebar {
  layoutService = inject(LayoutService);
}
